import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { highlightCode } from "../lib/highlight.js";
import { useLocale } from "../lib/i18n.jsx";
import { methodClass } from "../lib/methods.js";
import Icon from "../theme/Icon.jsx";
import { TabList } from "../theme/Tabs.jsx";
import SchemaTable from "./SchemaTable.jsx";
import { buildSnippets } from "./snippets.js";
import TagIntro from "./TagIntro.jsx";
import { type BaseUrlVar, type FieldsOf, type Group, type Operation, type Param, type ResponseInfo } from "./types.ts";

const STATUS_TYPE_CLASS: Record<string, string> = { ok: "sn-ok", warn: "sn-warn", err: "sn-err" };

// Altura a partir da qual o bloco colapsa. ~420px são cerca de 22 linhas: o
// bastante para ver a forma da resposta sem que um schema profundo empurre o
// próximo endpoint para fora da tela.
const COLLAPSED_HEIGHT = 420;

// Linhas que o bloco monta enquanto está fechado. O recorte de 420px mostra
// ~21; 48 dá folga para qualquer variação de fonte sem nunca deixar o degradê
// do corte cair sobre o vazio.
//
// O resto do texto não é jogado fora — ele continua inteiro em `source`, e
// entra no DOM quando (e se) alguém expandir. É a diferença entre guardar 8 MB
// de HTML realçado na tela e guardar as 48 linhas que a pessoa consegue ver.
const PREVIEW_LINES = 48;

// Bloco de código com altura limitada e botão de expandir.
//
// O controle só aparece quando o conteúdo realmente transborda — um `POST
// /authenticate` que responde 4 linhas não ganha um botão inútil.
//
// A medição é do <pre>: `scrollHeight` reporta a altura total do conteúdo mesmo
// com `max-height` aplicado, então o valor não depende de o bloco estar
// colapsado ou não. (Medir o <code> interno não funciona — ele é inline, e
// `scrollHeight` em elemento inline não reflete a altura real do texto.)
function CollapsibleCode({ source, className }: { source: string; className?: string }) {
  const [expanded, setExpanded] = useState(false);
  // Guardar a altura total (em vez de só "transborda: sim/não") permite animar
  // entre dois valores concretos de max-height. Transição para `none` ou `auto`
  // não existe em CSS: o bloco saltaria.
  const [fullHeight, setFullHeight] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const preRef = useRef<HTMLPreElement | null>(null);
  const codeRef = useRef<HTMLElement | null>(null);
  const { t } = useLocale();

  const lines = useMemo(() => (source ? source.split("\n") : []), [source]);
  const total = lines.length;
  // Enquanto fechado, só as primeiras PREVIEW_LINES viram HTML. Não depende da
  // medição: se dependesse, o primeiro render (fullHeight ainda 0) montaria o
  // bloco inteiro, que é exatamente o que se quer evitar.
  const truncado = !expanded && total > PREVIEW_LINES;
  const visivel = useMemo(
    () => (truncado ? lines.slice(0, PREVIEW_LINES).join("\n") : source),
    [truncado, lines, source]
  );
  const html = useMemo(() => highlightCode(visivel), [visivel]);

  useLayoutEffect(() => {
    const pre = preRef.current;
    const code = codeRef.current;
    if (!pre || !code) return undefined;

    const measure = () => {
      // Mede o <code>, não o <pre>.
      //
      // `pre.scrollHeight` parece a escolha óbvia, mas nunca é menor que
      // `clientHeight` — com um max-height aplicado ele devolve o próprio
      // limite. Como o limite vem daqui, cada medição realimentava a anterior
      // e o bloco crescia sem parar a cada tick do ResizeObserver.
      //
      // O <code> tem `display: block` (ver motion.css) e altura própria, que
      // não muda com o max-height do pai: medição estável por construção.
      const style = getComputedStyle(pre);
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      // A barra de rolagem horizontal ocupa altura dentro do pre. Esta diferença
      // continua correta mesmo com o pre clampado, e não realimenta.
      const scrollbar = pre.offsetHeight - pre.clientHeight;
      // Com o bloco truncado, `code.offsetHeight` é a altura das linhas
      // RENDERIZADAS, não a do texto inteiro — e é da altura inteira que o
      // max-height da animação precisa. Como `white-space: pre` não deixa linha
      // quebrar, toda linha tem a mesma altura, e a regra de três dá o total
      // sem precisar montar o resto do DOM para medi-lo.
      const renderizadas = truncado ? Math.min(PREVIEW_LINES, total) : total;
      const porLinha = code.offsetHeight / Math.max(1, renderizadas);
      setFullHeight(Math.ceil(porLinha * total + padding + scrollbar));
    };
    measure();

    // A fonte mono ainda pode estar carregando quando isto roda pela primeira
    // vez, e a altura muda quando ela entra.
    const observer = new ResizeObserver(measure);
    observer.observe(code);
    return () => observer.disconnect();
  }, [source, truncado, total]);

  // Margem de tolerância: colapsar para esconder 30px de conteúdo só troca
  // rolagem por um clique.
  const overflows = fullHeight > COLLAPSED_HEIGHT + 32;

  const toggle = useCallback(() => {
    setExpanded((wasExpanded) => {
      // Ao recolher um bloco longo, tudo abaixo sobe de uma vez e o leitor fica
      // olhando para outro endpoint. Reancorar no topo do bloco mantém o lugar.
      if (wasExpanded && wrapRef.current) {
        requestAnimationFrame(() => {
          const top = wrapRef.current?.getBoundingClientRect().top ?? 0;
          if (top < 0) window.scrollBy({ top, behavior: "instant" });
        });
      }
      return !wasExpanded;
    });
  }, []);

  const collapsed = overflows && !expanded;

  return (
    <div ref={wrapRef} className={`ref-code-collapse ${collapsed ? "collapsed" : ""} ${overflows ? "has-toggle" : ""}`}>
      {/* O viewport existe para ancorar o degradê do corte: o ::after precisa
          ficar no rodapé do código, não no rodapé do wrapper (que inclui o
          botão). */}
      <div className="ref-code-viewport">
        <pre
          ref={preRef}
          className={className}
          // Antes da primeira medição não há max-height: o bloco renderiza na
          // altura natural e o useLayoutEffect ajusta antes do primeiro paint.
          style={overflows ? { maxHeight: collapsed ? COLLAPSED_HEIGHT : fullHeight } : undefined}
        >
          <code ref={codeRef} dangerouslySetInnerHTML={{ __html: html }} />
        </pre>
      </div>

      {overflows && (
        <button type="button" className="ref-code-toggle" onClick={toggle}>
          <Icon name={expanded ? "chevron-up" : "chevron-down"} size={13} />
          <span>{expanded ? t("collapse") : t("expand")}</span>
          {!expanded && <span className="ref-code-toggle-n mono">{t("lines", { n: total })}</span>}
        </button>
      )}
    </div>
  );
}

function Params({ params }: { params: Param[] }) {
  const { t } = useLocale();
  if (!params?.length) return <div className="ref-noparams mono">{t("noParams")}</div>;
  return (
    <div className="ref-params">
      {params.map((p) => (
        <div key={`${p.loc}:${p.name}`} className="ref-param">
          <div className="ref-param-head">
            <code className="ref-param-name">{p.name}</code>
            <span className="ref-param-loc">{p.loc}</span>
            <span className="ref-param-type mono">{p.type}</span>
            {p.required && <span className="ref-param-req">{t("required")}</span>}
            {p.deprecated && <span className="ref-param-dep">{t("deprecated")}</span>}
          </div>
          {p.desc && <div className="ref-param-desc">{p.desc}</div>}
        </div>
      ))}
    </div>
  );
}

function RequestPanel({ op }: { op: Operation }) {
  const { t } = useLocale();
  const { snippets, langs } = useMemo(() => buildSnippets(op), [op]);
  const [lang, setLang] = useState(0);
  const index = snippets[langs[lang] ?? ""] ? lang : 0;
  const active = langs[index] ?? "";

  return (
    <>
      <div className="ref-panel-head">
        <span className={`ref-method ${methodClass(op.method)}`}>{op.method}</span>
        <span className="ref-panel-label">{t("request")}</span>
        <TabList variant="inline" labels={langs} active={index} onSelect={setLang} />
      </div>
      <CollapsibleCode source={snippets[active] ?? ""} className="ref-panel-code" />
    </>
  );
}

function ResponsePanel({ responses }: { responses: ResponseInfo[] }) {
  const { t } = useLocale();
  // Só as respostas com exemplo viram aba; um 404 sem corpo não merece painel.
  const withBody = responses.filter((r) => r.example);
  const [status, setStatus] = useState(0);
  if (!withBody.length) return null;

  const index = status < withBody.length ? status : 0;
  const active = withBody[index];
  if (!active) return null;

  return (
    <>
      <div className="ref-panel-head ref-panel-head-res">
        <span className="ref-panel-label">{t("response")}</span>
        <TabList
          variant="inline"
          labels={withBody.map((r) => r.status)}
          titles={withBody.map((r) => r.description)}
          active={index}
          onSelect={setStatus}
        />
      </div>
      <CollapsibleCode source={active.example ?? ""} className="ref-panel-code" />
    </>
  );
}

// ── Por que memo ──
// O useScrollSpy da ApiPage guarda a âncora corrente em estado, e ela muda
// dezenas de vezes ao percorrer a página. Cada mudança re-renderizava a
// referência inteira: 33 operações, com o realce de sintaxe refeito em cada
// bloco de código de cada uma. Era 1,4 s de JS só para rolar.
//
// `op` vem de um useMemo e `fieldsOf` do spec normalizado — as duas referências
// são estáveis entre renders, então a comparação rasa do memo corta a cascata
// inteira e a mudança de âncora fica no que ela de fato mexe: o índice lateral.
const Endpoint = memo(function Endpoint({ op, fieldsOf }: { op: Operation; fieldsOf: FieldsOf }) {
  const { t } = useLocale();
  // Campos da primeira resposta que tenha schema — na prática o 200.
  const responseSchema = op.responses.find((r) => r.schema)?.schema || null;
  return (
    <div className="ref-endpoint" id={op.id}>
      <h2 className="ref-h2">
        <span className={`ref-method ${methodClass(op.method)}`}>{op.method}</span>
        <code className="ref-path">{op.path}</code>
        {op.restricted && (
          <span className="ref-restricted">
            <Icon name="shield" size={12} />
            {op.restricted}
          </span>
        )}
        {op.deprecated && <span className="ref-param-dep">{t("deprecated")}</span>}
      </h2>

      <div className="ref-grid">
        <div className="ref-left">
          <div className="ref-endpoint-title">{op.title}</div>
          {op.desc && <p className="doc-p">{op.desc}</p>}

          {op.rateLimit && (
            <div className="ref-inline-note rl">
              <Icon name="gauge" size={13} />
              <span>
                <strong>{t("rateLimit")}:</strong> {op.rateLimit}
              </span>
            </div>
          )}

          {op.body?.example && (
            <>
              <div className="ref-subhead">{t("requestBody")}</div>
              {/* Mesmo tratamento do painel: com spec real, um requestBody de
                  schema profundo fica tão alto quanto a resposta. */}
              <CollapsibleCode source={op.body.example} className="doc-code" />
            </>
          )}

          {/* O exemplo mostra a forma; a tabela mostra o significado. Ela se
              esconde sozinha quando o schema não tem campos. */}
          {op.body?.schema && <SchemaTable schema={op.body.schema} fieldsOf={fieldsOf} title={t("requestFields")} />}

          <div className="ref-subhead">{t("params")}</div>
          <Params params={op.params} />

          {responseSchema && <SchemaTable schema={responseSchema} fieldsOf={fieldsOf} title={t("responseFields")} />}

          {(op.statusNotes?.length ?? 0) > 0 && (
            <>
              <div className="ref-subhead">{t("statusHere")}</div>
              <div className="ref-status-notes">
                {(op.statusNotes ?? []).map((s) => (
                  <div key={s.code} className={`ref-status-note ${(s.type ? STATUS_TYPE_CLASS[s.type] : "") ?? ""}`}>
                    <code>{s.code}</code>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {op.bestPractice && (
            <div className="doc-callout doc-callout-tip">
              <div className="doc-callout-title">{t("bestPractice")}</div>
              <div>{op.bestPractice}</div>
            </div>
          )}
        </div>

        <div className="ref-right">
          <div className="ref-panel">
            <RequestPanel op={op} />
            <ResponsePanel responses={op.responses} />
          </div>
        </div>
      </div>
    </div>
  );
});

// `operations` chega já na ordem do índice lateral (agrupada por recurso). Sem
// ela, o conteúdo seguiria a ordem do spec e o destaque do índice saltaria
// entre grupos conforme a página rola.
// Descrições vindas do spec podem trazer HTML simples (<code>, <b>, <br/>), que é
// o que o Swagger UI aceita e o que as anotações do backend já usam.
const SpecHtml = ({ html, className }: { html: string; className?: string }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

interface ReferenceProps {
  group: Group;
  operations: Operation[];
  baseUrl: string;
  baseUrlVars?: BaseUrlVar[];
  serverDescription?: string;
  fieldsOf: FieldsOf;
}

export default function Reference({
  group,
  operations,
  baseUrl,
  baseUrlVars = [],
  serverDescription,
  fieldsOf,
}: ReferenceProps) {
  const { t, tagLabel } = useLocale();
  const list = operations || group.operations;
  return (
    <>
      <div className="doc-eyebrow">{t("apiReference")}</div>
      <h1 className="doc-title">{tagLabel(group.name)}</h1>
      {/* As descriptions do spec vêm com HTML simples (<b>, <code>, <br/>), que é
          o que o Swagger UI aceita e o que as anotações do backend já usam. */}
      {group.description && <SpecHtml className="doc-lead" html={group.description} />}
      <TagIntro tagId={group.id} />
      {baseUrl && (
        <>
          <div className="ref-baseurl">
            <span className="ref-baseurl-label mono">{t("baseUrl")}</span>
            <code className="mono">{baseUrl}</code>
          </div>
          {/* Sem isto o leitor vê "{domain}" e não sabe se substitui, se é literal
              ou se a doc está quebrada. */}
          {baseUrlVars.length > 0 && (
            <div className="ref-server-vars">
              {serverDescription && <SpecHtml className="ref-server-note" html={serverDescription} />}
              {baseUrlVars.map((v) => (
                <div key={v.name} className="ref-server-var">
                  <code className="ref-param-name">{`{${v.name}}`}</code>
                  {v.enum && <span className="ref-param-type mono">{v.enum.join(" | ")}</span>}
                  {v.description && <SpecHtml className="ref-param-desc" html={v.description} />}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {list.map((op) => (
        <Endpoint key={op.id} op={op} fieldsOf={fieldsOf} />
      ))}
    </>
  );
}
